/**
 * Which parts of Wend a workspace has — the control both staff forms share
 * (`/tenants/new` and the correction form on `/tenants/:tenantId`).
 *
 * ## TOGGLE CHIPS, AND WHY THE DESCRIPTIONS STAYED
 *
 * Until slice 66 each form drew its own grid of four checkbox cards, with the
 * part's name and one line about it inside each card. The Blueprint's scope
 * chips (`@wend/ui`'s `ScopeChips`, `selection="multiple"`) replace the
 * checkboxes: a row of `<button aria-pressed>` in a named group, pressed ones in
 * violet. Same behaviour — each press adds or removes one part, nothing is sent
 * until the form is saved — in a fraction of the height.
 *
 * The one line per part did **not** go. It is what tells a staff member what
 * they are granting — until slice 71 Flow's said "Journeys — not built yet.",
 * which is what stopped anyone selling it; since Flow shipped it says what is
 * built and that it sends through Connect (`lib/features.ts`). So the four
 * lines are listed under the chips, every part
 * whether pressed or not, as a small description list. Putting them inside the
 * chips was rejected: a chip that wraps a sentence is a card again, and the
 * screen reader would read the whole sentence as the button's name on every
 * press.
 *
 * ## ORDER IS REBUILT FROM `FEATURES`
 *
 * `ScopeChips` hands back the pressed values in press order. The forms want
 * the display order (`PILLAR_ORDER`), so the array is rebuilt from `FEATURES`
 * on every change — the same thing each form's `toggleFeature` did before this
 * file existed. `renderFeatureSet` sorts again before anything is audited, so
 * order never reads as a change either way.
 *
 * ## THE FIELDSET STAYS
 *
 * The legend ("Features *", required) names the whole control for a screen
 * reader and carries the asterisk, exactly as before; the chip group inside it
 * has its own `aria-label`. The error, when there is one, is a `role="alert"`
 * line under the list, as it was.
 *
 * THE HOLE: there is no "at least one" guard here. Both forms validate that
 * with the shared zod schema on submit and show the error this component is
 * handed; a control that refused to un-press the last chip would hide why.
 */
import { useId, type ReactNode } from 'react';

import type { Feature } from '@wend/shared';
import { KICKER_CLASS, Label, ScopeChips, cn } from '@wend/ui';

import { FEATURE_COPY, FEATURES } from '../lib/features';

export interface FeaturePickerProps {
  /** The legend's words — "Features". */
  legend: ReactNode;
  /** One or two sentences under the legend: what turning a part on or off does. */
  description: ReactNode;
  value: readonly Feature[];
  onChange: (next: Feature[]) => void;
  error?: string;
}

export function FeaturePicker({ legend, description, value, onChange, error }: FeaturePickerProps) {
  // Each chip points at its own description, and the group at the fieldset's
  // description and the error. The old checkboxes carried the description
  // inside their label, so it was part of what each control said; as separate
  // elements they are linked by id or they are lost (slice 66 review).
  const baseId = useId();
  const descriptionId = `${baseId}-description`;
  const errorId = `${baseId}-error`;
  const partId = (feature: Feature): string => `${baseId}-part-${feature}`;
  const items = FEATURES.map((feature) => ({
    value: feature,
    label: FEATURE_COPY[feature].label,
    describedBy: partId(feature),
  }));

  return (
    <fieldset className="flex flex-col gap-2">
      <Label asChild>
        <legend>
          {legend}
          <span aria-hidden="true" className="ml-1 text-brand-b">
            *
          </span>
          <span className="sr-only"> (required)</span>
        </legend>
      </Label>

      <p id={descriptionId} className="-mt-0.5 max-w-prose text-[0.8rem] leading-snug text-muted">
        {description}
      </p>

      <ScopeChips
        className="mt-1"
        label="Parts of Wend this workspace has"
        selection="multiple"
        items={items}
        aria-describedby={error === undefined ? descriptionId : `${descriptionId} ${errorId}`}
        aria-invalid={error === undefined ? undefined : true}
        value={value}
        onValueChange={(next) => {
          // Rebuilt from FEATURES so the array always keeps the display order.
          onChange(FEATURES.filter((feature) => next.includes(feature)));
        }}
      />

      <dl className="mt-1.5 grid gap-x-6 gap-y-1.5 sm:grid-cols-2">
        {FEATURES.map((feature) => (
          <div key={feature} className="flex min-w-0 items-baseline gap-2.5">
            <dt className={cn(KICKER_CLASS, 'w-16 shrink-0', value.includes(feature) ? 'text-brand-ink' : 'text-muted')}>
              {FEATURE_COPY[feature].label}
            </dt>
            <dd id={partId(feature)} className="min-w-0 text-[0.78rem] leading-snug text-muted">
              {FEATURE_COPY[feature].description}
            </dd>
          </div>
        ))}
      </dl>

      {error !== undefined && (
        <p id={errorId} role="alert" className="font-mono text-[0.72rem] leading-snug tracking-[0.04em] text-bad">
          {error}
        </p>
      )}
    </fieldset>
  );
}
